import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { api } from "../lib/api";
import { useSeo } from "../lib/ui";

export default function NewsDetail() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useSeo({
    title: data ? `${data.story.title} — KickStartKenyaFootball` : "Story",
    description: data?.story?.excerpt,
  });

  useEffect(() => {
    setData(null);
    api.get(`/news/${slug}`).then((r) => setData(r.data)).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound)
    return (
      <div className="mx-auto max-w-[1500px] px-5 py-40 md:px-10" data-testid="news-not-found">
        <h1 className="display-2">Story not found</h1>
        <Link to="/news" className="btn-ghost mt-8">Back to newsroom</Link>
      </div>
    );
  if (!data) return <div className="py-40 text-center text-zinc-400">Loading…</div>;

  const { story, more } = data;

  return (
    <article data-testid="news-detail-page">
      <div className="border-b border-white/10 bg-[#0b0b0b] px-5 pb-14 pt-12 md:px-10 md:pt-16">
        <div className="mx-auto max-w-[1000px]">
          <Link to="/news" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.16em] text-white/70 hover:text-ochre" data-testid="news-back">
            <ArrowLeft size={14} /> Newsroom
          </Link>
          <p className="overline mt-8 text-ochre">{story.category} · {story.date}</p>
          <h1 className="display-2 mt-4" data-testid="news-title">{story.title}</h1>
          {story.sources?.length ? (
            <p className="mono mt-6 flex flex-wrap items-center gap-3 text-[0.6rem] uppercase tracking-[0.16em] text-zinc-400">
              <span className="border border-ochre px-2 py-1 text-ochre" data-testid="news-verified-tag">
                Sources checked
              </span>
              {story.sources.length} online {story.sources.length === 1 ? "source" : "sources"}
            </p>
          ) : null}
        </div>
      </div>

      <div className="mx-auto max-w-[760px] px-5 py-16 md:px-10 md:py-24">
        <p className="serif text-xl italic leading-relaxed text-zinc-300 md:text-2xl">{story.excerpt}</p>
        <div
          className="mt-10 space-y-6 text-base leading-relaxed text-zinc-300 [&_a]:text-ochre [&_h2]:font-display [&_h2]:text-2xl [&_h2]:uppercase [&_h2]:text-white [&_p]:mb-6 [&_strong]:text-white"
          data-testid="news-body"
          dangerouslySetInnerHTML={{ __html: story.body }}
        />

        <div className="mt-14 border-t border-white/15 pt-8" data-testid="news-sources">
          <p className="overline text-ochre">Sources and verification</p>
          {story.sources?.length ? (
            <ul className="mt-5 space-y-3">
              {story.sources.map((s, i) => {
                const src = typeof s === "string" ? { url: s, label: "Reported source", publisher: s } : s;
                return (
                <li key={src.url + i} className="border-l-2 border-maasai pl-4">
                  <a
                    href={src.url}
                    target="_blank"
                    rel="noreferrer"
                    data-testid={`news-source-${i}`}
                    className="text-sm text-ochre underline underline-offset-4 hover:text-white"
                  >
                    {src.label}
                  </a>
                  <span className="mono mt-1 block text-[0.6rem] uppercase tracking-[0.16em] text-zinc-500">
                    {src.publisher}
                  </span>
                </li>
                );
              })}
            </ul>
          ) : (
            <p className="mt-4 text-sm text-zinc-400" data-testid="news-sources-empty">
              This story is awaiting source verification and is not yet listed in the live file.
            </p>
          )}
          {story.source_url ? (
            <a
              href={story.source_url}
              target="_blank"
              rel="noreferrer"
              className="mono mt-6 inline-block text-[0.6rem] uppercase tracking-[0.16em] text-zinc-400 underline underline-offset-4 hover:text-ochre"
              data-testid="news-primary-source"
            >
              Primary source
            </a>
          ) : null}
          <p className="mt-6 text-xs leading-relaxed text-zinc-500">
            {story.verification_note ||
              "Every claim about the wider game is linked to a public source above. Statements about our own programme are ours, and are labelled as proposals rather than agreed or contracted work."}
          </p>
        </div>
      </div>

      {more?.length ? (
        <div className="border-t border-zinc-900 bg-[#0b0b0b] py-20">
          <div className="mx-auto max-w-[1500px] px-5 md:px-10">
            <p className="overline">More stories</p>
            <div className="mt-10 grid gap-5 sm:grid-cols-3" data-testid="news-more">
              {more.map((n) => (
                <Link
                  key={n.id}
                  to={`/news/${n.slug}`}
                  className="group flex min-h-[180px] flex-col justify-between border border-white/10 bg-[#0d0d0d] p-6 transition-colors hover:border-maasai"
                >
                  <div>
                    <span className="text-[0.62rem] font-bold uppercase tracking-[0.22em] text-ochre">{n.category}</span>
                    <h3 className="mt-3 font-display text-base font-semibold uppercase leading-tight text-white transition-colors group-hover:text-ochre">
                      {n.title}
                    </h3>
                  </div>
                  <ArrowRight size={15} className="mt-5 text-ochre transition-transform group-hover:translate-x-1" />
                </Link>
              ))}
            </div>
          </div>
        </div>
      ) : null}
    </article>
  );
}
